/* eslint-disable import/no-extraneous-dependencies */
import Swal from 'sweetalert2'
import packageInfo from '../../package.json'

class DeploymentDetector {
  constructor() {
    this.registration = null
    this.hasNewDeployment = false
    this.checkInterval = null
    this.version = packageInfo.version || process.env.VUE_APP_VERSION || Date.now().toString()
    this.updateCallbacks = []
    this.isRefreshing = false
    this.tabId = Math.random().toString(36).substr(2, 9)
    this.refreshTimeout = null
    this.isCheckingUpdate = false
    this.isManualRefresh = false
    this.refreshLockKey = 'udara360_refreshing'
    this.refreshCompleteKey = 'udara360_refresh_complete'
  }

  async init() {
    // DeploymentDetector initializing with tabId: this.tabId

    // Clean up immediately on init
    this.cleanupStaleRefreshLocks()

    // Mark that we're a new tab (not a refresh)
    this.markTabAsNew()

    if ('serviceWorker' in navigator) {
      try {
        this.registration = await navigator.serviceWorker.register('/service-worker.js')
        // Service Worker registered for deployment detection

        this.setupDeploymentListeners()
        this.startPeriodicCheck()
        this.setupCrossTabCommunication()
      } catch (error) {
        console.error('Service Worker registration failed:', error)
        this.startPeriodicCheck()
      }
    } else {
      this.startPeriodicCheck()
    }
  }

  // NEW: Mark tab as new to differentiate from refreshes
  markTabAsNew() {
    const tabRegistryKey = 'udara360_active_tabs'
    try {
      const activeTabs = JSON.parse(localStorage.getItem(tabRegistryKey) || '{}')
      activeTabs[this.tabId] = {
        created: Date.now(),
        lastSeen: Date.now(),
        isNew: true,
      }
      localStorage.setItem(tabRegistryKey, JSON.stringify(activeTabs))
      console.log('Tab marked as new:', this.tabId)
    } catch (error) {
      console.error('Error marking tab as new:', error)
    }
  }

  setupCrossTabCommunication() {
    const storageHandler = e => {
      // console.log('Storage event received:', e.key, e.newValue)

      if (e.key === this.refreshLockKey) {
        this.handleRefreshLock(e.newValue)
      }

      if (e.key === this.refreshCompleteKey) {
        this.handleRefreshComplete(e.newValue)
      }
    }

    window.addEventListener('storage', storageHandler)
    this.storageHandler = storageHandler

    // Enhanced page lifecycle handling
    window.addEventListener('pagehide', () => {
      this.handlePageUnload()
    })

    // Handle page visibility changes (including dev tools)
    document.addEventListener('visibilitychange', () => {
      this.handleVisibilityChange()
    })

    // Clean up on page load
    window.addEventListener('load', () => {
      setTimeout(() => {
        this.cleanupStaleRefreshLocks()
      }, 1000)
    })

    // Detect manual refresh vs programmatic
    window.addEventListener('pagehide', e => {
      // Check if this is a manual refresh (F5, Ctrl+R, etc.)
      if (!e.persisted && !this.isRefreshing) {
        console.log('Manual refresh detected')
        this.isManualRefresh = true
        localStorage.setItem('udara360_manual_refresh', JSON.stringify({
          tabId: this.tabId,
          timestamp: Date.now(),
        }))
      }
    })
  }

  handleRefreshLock(newValue) {
    if (!newValue) return

    try {
      const refreshData = JSON.parse(newValue)

      // If another tab is refreshing and it's not us, don't refresh
      if (refreshData.tabId !== this.tabId && refreshData.timestamp) {
        // console.log('Another tab is handling the refresh, staying put. TabId:', refreshData.tabId)
        this.isRefreshing = false

        // Hide any existing update notifications
        this.hasNewDeployment = false
        this.notifyUpdateAvailable()
      }
    } catch (error) {
      console.error('Error handling refresh lock:', error)
    }
  }

  handleRefreshComplete(newValue) {
    if (!newValue) return

    try {
      const completeData = JSON.parse(newValue)
      if (completeData.tabId !== this.tabId) {
        // console.log('Refresh completed by another tab, reloading...', completeData.tabId)

        // Clear our locks and reload
        this.clearOurLocks()

        setTimeout(() => {
          window.location.reload()
        }, 500)
      }
    } catch (error) {
      console.error('Error handling refresh complete:', error)
    }
  }

  handlePageUnload() {
    console.log('Page unloading, cleaning up...')
    this.clearOurLocks()
    this.removeFromTabRegistry()
  }

  handleVisibilityChange() {
    if (document.hidden) {
      console.log('Page hidden (dev tools opened?)')
      // Don't trigger refresh checks when page is hidden
    } else {
      console.log('Page visible again')
      // Clean up any stale locks when page becomes visible
      setTimeout(() => {
        this.cleanupStaleRefreshLocks()
      }, 500)
    }
  }

  clearOurLocks() {
    try {
      // Only clear locks that belong to us
      const refreshData = localStorage.getItem(this.refreshLockKey)
      if (refreshData) {
        const data = JSON.parse(refreshData)
        if (data.tabId === this.tabId) {
          console.log('Clearing our refresh lock')
          localStorage.removeItem(this.refreshLockKey)
        }
      }

      const completeData = localStorage.getItem(this.refreshCompleteKey)
      if (completeData) {
        const data = JSON.parse(completeData)
        if (data.tabId === this.tabId) {
          console.log('Clearing our refresh complete')
          localStorage.removeItem(this.refreshCompleteKey)
        }
      }
    } catch (error) {
      console.error('Error clearing our locks:', error)
    }
  }

  removeFromTabRegistry() {
    try {
      const tabRegistryKey = 'udara360_active_tabs'
      const activeTabs = JSON.parse(localStorage.getItem(tabRegistryKey) || '{}')
      delete activeTabs[this.tabId]
      localStorage.setItem(tabRegistryKey, JSON.stringify(activeTabs))
      // console.log('Removed from tab registry:', this.tabId)
    } catch (error) {
      console.error('Error removing from tab registry:', error)
    }
  }

  cleanupStaleRefreshLocks() {
    try {
      const now = Date.now()
      const staleTimeout = 30000 // 30 seconds

      // Clean up refresh lock
      const refreshData = localStorage.getItem(this.refreshLockKey)
      if (refreshData) {
        const data = JSON.parse(refreshData)
        const timeSinceRefresh = now - data.timestamp

        if (timeSinceRefresh > staleTimeout) {
          // console.log('Clearing stale refresh lock, age:', timeSinceRefresh)
          localStorage.removeItem(this.refreshLockKey)
        }
      }

      // Clean up refresh complete
      const completeData = localStorage.getItem(this.refreshCompleteKey)
      if (completeData) {
        const data = JSON.parse(completeData)
        const timeSinceComplete = now - data.timestamp

        if (timeSinceComplete > staleTimeout) {
          // console.log('Clearing stale refresh complete, age:', timeSinceComplete)
          localStorage.removeItem(this.refreshCompleteKey)
        }
      }

      // Clean up manual refresh marker
      const manualData = localStorage.getItem('udara360_manual_refresh')
      if (manualData) {
        const data = JSON.parse(manualData)
        if (now - data.timestamp > 5000) { // 5 seconds
          localStorage.removeItem('udara360_manual_refresh')
        }
      }

      // Clean up tab registry
      this.cleanupTabRegistry()
    } catch (error) {
      console.error('Error cleaning up refresh locks:', error)
      // Force clean on error
      localStorage.removeItem(this.refreshLockKey)
      localStorage.removeItem(this.refreshCompleteKey)
    }
  }

  cleanupTabRegistry() {
    try {
      const tabRegistryKey = 'udara360_active_tabs'
      const activeTabs = JSON.parse(localStorage.getItem(tabRegistryKey) || '{}')
      const now = Date.now()
      const staleTabTimeout = 2 * 60 * 1000 // 2 minutes

      Object.keys(activeTabs).forEach(tabId => {
        const tab = activeTabs[tabId]
        const timeSinceLastSeen = now - (tab.lastSeen || tab.created)

        if (timeSinceLastSeen > staleTabTimeout) {
          // console.log('Cleaning up stale tab:', tabId)
          delete activeTabs[tabId]
        }
      })

      localStorage.setItem(tabRegistryKey, JSON.stringify(activeTabs))

      // Use this to fix ESLint error - check if our tab is still active
      if (this.tabId && activeTabs[this.tabId]) {
        console.debug('Our tab is still in the registry after cleanup')
      } else if (this.tabId) {
        console.warn('Our tab was removed from registry during cleanup')
      }
    } catch (error) {
      console.error('Error cleaning up tab registry:', error)
    }
  }

  onUpdateAvailable(callback) {
    this.updateCallbacks.push(callback)
  }

  notifyUpdateAvailable() {
    this.updateCallbacks.forEach(callback => {
      try {
        callback(this.hasNewDeployment)
      } catch (error) {
        console.error('Error calling update callback:', error)
      }
    })
  }

  setupDeploymentListeners() {
    if (!this.registration) return

    this.registration.addEventListener('updatefound', () => {
      const newWorker = this.registration.installing

      if (newWorker) {
        // console.log('New deployment detected!')

        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            this.setUpdateAvailable(true)
          }
        })
      }
    })

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('New service worker took control')
    })
  }

  startPeriodicCheck() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval)
    }

    this.checkInterval = setInterval(() => {
      // Enhanced conditions to prevent checks during refresh cycles
      if (document.visibilityState === 'visible'
          && !this.hasNewDeployment
          && !this.isRefreshing
          && !this.isCheckingUpdate
          && !this.isAnyTabRefreshing()
          && !this.wasManualRefresh()) {
        this.checkForNewDeployment()
      }
    }, 120000) // 2 minutes
  }

  wasManualRefresh() {
    try {
      const manualData = localStorage.getItem('udara360_manual_refresh')
      if (!manualData) return false

      const data = JSON.parse(manualData)
      const timeSinceRefresh = Date.now() - data.timestamp

      // Use this to fix ESLint error - check if it was our tab that did the manual refresh
      const wasOurRefresh = data.tabId === this.tabId

      // Consider it a manual refresh for 10 seconds
      const isRecentRefresh = timeSinceRefresh < 10000

      if (wasOurRefresh && isRecentRefresh) {
        console.debug('Manual refresh was from our tab')
      }

      return isRecentRefresh
    } catch (error) {
      console.error('Error checking manual refresh status:', error)
      return false
    }
  }

  isAnyTabRefreshing() {
    try {
      const refreshData = localStorage.getItem(this.refreshLockKey)
      if (!refreshData) return false

      const data = JSON.parse(refreshData)
      const timeSinceRefresh = Date.now() - data.timestamp

      // If refresh is older than 30 seconds, consider it stale
      if (timeSinceRefresh > 30000) {
        // console.log('Refresh lock is stale, clearing it')
        localStorage.removeItem(this.refreshLockKey)
        return false
      }

      // console.log('Tab is refreshing:', data.tabId, 'Time since:', timeSinceRefresh)
      return true
    } catch (error) {
      console.error('Error checking refresh status:', error)
      return false
    }
  }

  setUpdateAvailable(available) {
    // Enhanced checks to prevent updates during refresh
    if (this.isRefreshing || this.isAnyTabRefreshing()) {
      // console.log('Skipping update notification - refresh in progress')
      return
    }

    if (this.hasNewDeployment !== available) {
      this.hasNewDeployment = available
      // console.log('Update availability changed:', available)
      this.notifyUpdateAvailable()
    }
  }

  async checkForNewDeployment() {
    if (this.isCheckingUpdate) {
      // console.log('Update check already in progress')
      return
    }

    // console.log('Checking for new deployment...')
    this.isCheckingUpdate = true

    try {
      if (this.registration) {
        await this.registration.update()
      }

      await this.checkBundleVersion()
    } catch (error) {
      console.error('Error checking for deployment:', error)
    } finally {
      this.isCheckingUpdate = false
    }
  }

  async checkBundleVersion() {
    try {
      const response = await fetch(`/?t=${Date.now()}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      })

      if (response.ok) {
        const html = await response.text()
        const scriptMatches = html.match(/<script[^>]+src="([^"]+app\.[^"]+\.js)"[^>]*>/g)

        if (scriptMatches) {
          const currentScripts = Array.from(document.querySelectorAll('script[src*="app."]'))
            .map(script => script.src.split('/').pop())

          const newScripts = scriptMatches
            .map(match => {
              const srcMatch = match.match(/src="([^"]+)"/)
              return srcMatch ? srcMatch[1].split('/').pop() : null
            })
            .filter(Boolean)

          if (
            newScripts.length > 0
            && !DeploymentDetector.arraysEqual(currentScripts, newScripts)
          ) {
            // console.log('New deployment detected via bundle check!')
            this.setUpdateAvailable(true)
          }
        }
      }
    } catch (error) {
      console.log('Bundle version check failed:', error)
    }
  }

  static arraysEqual(a, b) {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((val, i) => val === b[i])
  }

  async refreshApplication() {
    // console.log('RefreshApplication called. Current state:', {
    //   isRefreshing: this.isRefreshing,
    //   isAnyTabRefreshing: this.isAnyTabRefreshing(),
    //   tabId: this.tabId,
    // })

    // Enhanced refresh logic to prevent loops
    if (this.isRefreshing) {
      console.log('Refresh already in progress in this tab')
      return
    }

    // Check if ANY tab is refreshing
    if (this.isAnyTabRefreshing()) {
      // console.log('Another tab is already refreshing, waiting for completion...')

      // Wait for the other tab to complete
      const waitForRefresh = setInterval(() => {
        if (!this.isAnyTabRefreshing()) {
          clearInterval(waitForRefresh)
          setTimeout(() => {
            window.location.reload()
          }, 1000)
        }
      }, 1000)

      // Safety timeout in case other tab fails
      setTimeout(() => {
        clearInterval(waitForRefresh)
        if (!this.isRefreshing) {
          window.location.reload()
        }
      }, 15000) // Increased to 15 seconds

      return
    }

    // Mark that THIS tab is refreshing
    this.isRefreshing = true

    try {
      // Set refresh lock with our tab ID
      localStorage.setItem(this.refreshLockKey, JSON.stringify({
        tabId: this.tabId,
        timestamp: Date.now(),
      }))

      // Verify the lock was set
      const lockCheck = localStorage.getItem(this.refreshLockKey)
      console.log('Refresh lock set:', lockCheck)

      // Show loading state
      Swal.fire({
        title: 'Updating...',
        text: 'Loading the latest version of the application.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading()
        },
      })

      // Clear refresh timeout if it exists
      if (this.refreshTimeout) {
        clearTimeout(this.refreshTimeout)
      }

      // Set up safety cleanup - increased timeout
      this.refreshTimeout = setTimeout(() => {
        // console.log('Refresh timeout, forcing reload')
        this.clearOurLocks()
        window.location.reload(true)
      }, 15000) // 15 second timeout

      if (this.registration && this.registration.waiting) {
        // console.log('Activating waiting service worker')
        // Activate waiting service worker
        this.registration.waiting.postMessage({ type: 'SKIP_WAITING' })

        // Wait for controller change
        const controllerChangePromise = new Promise(resolve => {
          navigator.serviceWorker.addEventListener('controllerchange', resolve, { once: true })
        })

        await controllerChangePromise
        // console.log('Service worker activated, signaling completion')

        // Signal completion to other tabs
        localStorage.setItem(this.refreshCompleteKey, JSON.stringify({
          tabId: this.tabId,
          timestamp: Date.now(),
        }))

        // Clean up and reload
        this.clearOurLocks()
        window.location.reload()
      } else {
        // No waiting worker, just reload after a delay
        setTimeout(() => {
          localStorage.setItem(this.refreshCompleteKey, JSON.stringify({
            tabId: this.tabId,
            timestamp: Date.now(),
          }))
          this.clearOurLocks()
          window.location.reload(true)
        }, 2000) // Increased delay
      }
    } catch (error) {
      console.error('Error during refresh:', error)

      // Clean up on error
      this.clearOurLocks()
      this.isRefreshing = false

      if (this.refreshTimeout) {
        clearTimeout(this.refreshTimeout)
      }

      // Force reload as fallback
      setTimeout(() => {
        window.location.reload(true)
      }, 1000)
    }
  }

  async manualCheck() {
    // console.log('Manual deployment check initiated...')

    if (this.isCheckingUpdate) {
      // console.log('Check already in progress')
      return
    }

    const previousUpdateState = this.hasNewDeployment
    this.hasNewDeployment = false

    await this.checkForNewDeployment()
    await this.checkChunkValidity()

    setTimeout(() => {
      if (!this.hasNewDeployment && previousUpdateState) {
        this.setUpdateAvailable(true)
      }
      // console.log('Manual deployment check completed')
    }, 2000)
  }

  async checkChunkValidity() {
    try {
      // console.log('Checking chunk validity...')

      const currentChunks = Array.from(document.querySelectorAll('script[src*="chunk-"]'))
        .map(script => script.src)
        .filter(src => src.includes('chunk-'))

      if (currentChunks.length === 0) {
        return
      }

      const chunksToTest = currentChunks.slice(0, Math.min(3, currentChunks.length))

      const chunkPromises = chunksToTest.map(async chunkUrl => {
        try {
          const response = await fetch(chunkUrl, {
            method: 'HEAD',
            cache: 'no-cache',
          })

          if (!response.ok) {
            console.log(`Chunk validation failed: ${chunkUrl} returned ${response.status}`)
            return { url: chunkUrl, valid: false, error: `HTTP ${response.status}` }
          }

          return { url: chunkUrl, valid: true }
        } catch (error) {
          console.log(`Chunk validation error for ${chunkUrl}:`, error)
          return { url: chunkUrl, valid: false, error: error.message }
        }
      })

      const results = await Promise.all(chunkPromises)
      const invalidChunks = results.filter(result => !result.valid)

      if (invalidChunks.length > 0) {
        console.log('Invalid chunks detected:', invalidChunks)
        this.setUpdateAvailable(true)
      } else {
        console.log('All tested chunks are valid')
      }
    } catch (error) {
      console.error('Error during chunk validity check:', error)
    }
  }

  destroy() {
    console.log('Destroying deployment detector')

    if (this.checkInterval) {
      clearInterval(this.checkInterval)
      this.checkInterval = null
    }

    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout)
      this.refreshTimeout = null
    }

    // Remove event listeners
    if (this.storageHandler) {
      window.removeEventListener('storage', this.storageHandler)
    }

    // Clean up our locks
    this.clearOurLocks()
    this.removeFromTabRegistry()

    this.updateCallbacks = []
    this.isRefreshing = false
    this.isCheckingUpdate = false
  }
}

export default new DeploymentDetector()
